import React, { useState, useEffect } from 'react';
import { collection, doc, getDoc, setDoc, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './UsersManagement.css';

function UsersManagement({ onClose, selectedProfile }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(['Admin']);
  const [householdName, setHouseholdName] = useState('Default Household');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'roles', 'household'
  const user = auth.currentUser;

  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadUsers(),
      loadRoles(),
      loadHouseholdName()
    ]);
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const usersDocRef = doc(db, 'users', user.uid, 'Profiles', 'Users');
      const snapshot = await getDoc(usersDocRef);
      
      if (snapshot.exists()) {
        const usersData = snapshot.data().users || [];
        setUsers(usersData);
      } else {
        // Create default admin profile
        const defaultProfile = {
          name: user.displayName || 'Primary Admin',
          type: 'Admin',
          pin: ''
        };
        await setDoc(usersDocRef, { users: [defaultProfile] });
        setUsers([defaultProfile]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const rolesDocRef = doc(db, 'users', user.uid, 'Profiles', 'Roles');
      const snapshot = await getDoc(rolesDocRef);
      
      if (snapshot.exists()) {
        const rolesData = snapshot.data().roles || ['Admin'];
        setRoles(rolesData);
      } else {
        await setDoc(rolesDocRef, { roles: ['Admin'] });
        setRoles(['Admin']);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadHouseholdName = async () => {
    try {
      const householdDocRef = doc(db, 'users', user.uid, 'Settings', 'Household');
      const snapshot = await getDoc(householdDocRef);
      
      if (snapshot.exists()) {
        setHouseholdName(snapshot.data().name || 'Default Household');
      }
    } catch (error) {
      console.error('Error loading household name:', error);
    }
  };

  const saveUsers = async (updatedUsers) => {
    try {
      const usersDocRef = doc(db, 'users', user.uid, 'Profiles', 'Users');
      await setDoc(usersDocRef, { users: updatedUsers });
      setUsers(updatedUsers);
      alert('Users saved successfully');
    } catch (error) {
      console.error('Error saving users:', error);
      alert('Failed to save users');
    }
  };

  const saveRoles = async (updatedRoles) => {
    try {
      const rolesDocRef = doc(db, 'users', user.uid, 'Profiles', 'Roles');
      await setDoc(rolesDocRef, { roles: updatedRoles });
      setRoles(updatedRoles);
      alert('Roles saved successfully');
    } catch (error) {
      console.error('Error saving roles:', error);
      alert('Failed to save roles');
    }
  };

  const saveHouseholdName = async (name) => {
    try {
      const householdDocRef = doc(db, 'users', user.uid, 'Settings', 'Household');
      await setDoc(householdDocRef, { name });
      setHouseholdName(name);
      alert('Household name saved successfully');
    } catch (error) {
      console.error('Error saving household name:', error);
      alert('Failed to save household name');
    }
  };

  const handleSetPin = async (userName, userType, newPin) => {
    const updatedUsers = users.map(u => {
      if (u.name === userName && u.type === userType) {
        return { ...u, pin: newPin };
      }
      return u;
    });
    await saveUsers(updatedUsers);
  };

  const handleRemovePin = async (userName, userType) => {
    const updatedUsers = users.map(u => {
      if (u.name === userName && u.type === userType) {
        const { pin, ...rest } = u;
        return rest;
      }
      return u;
    });
    await saveUsers(updatedUsers);
  };

  if (loading) {
    return (
      <div className="users-modal-overlay">
        <div className="users-modal">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-modal-overlay">
      <div className="users-modal">
        <div className="users-modal-header">
          <h2>User Management</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {/* User Info Card */}
        <div className="user-info-card">
          <div className="avatar-circle">
            {(user.displayName || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="user-details">
            <h3>{user.displayName || 'User'}</h3>
            <p>{user.email || 'No email'}</p>
            <div className="badges">
              <span className="badge badge-type">
                {user.providerData.some(p => p.providerId.includes('google')) ? 'Google' : 'Local'}
              </span>
              <span className="badge badge-household">{householdName}</span>
            </div>
          </div>
        </div>

        {/* Current Profile Info */}
        <div className="current-profile-card">
          <h4>Current Active Profile</h4>
          <div className="profile-info">
            <div className="profile-field">
              <label>Name:</label>
              <span>{selectedProfile?.name || '(None Selected)'}</span>
            </div>
            <div className="profile-field">
              <label>Type:</label>
              <span className="badge">{selectedProfile?.type || 'UNKNOWN'}</span>
            </div>
            <div className="profile-field">
              <label>PIN:</label>
              <span>{users.find(u => u.name === selectedProfile?.name && u.type === selectedProfile?.type)?.pin ? '••••' : 'Not Set'}</span>
            </div>
          </div>
          {selectedProfile?.name && (
            <SetPinDialog
              userName={selectedProfile.name}
              userType={selectedProfile.type}
              currentPin={users.find(u => u.name === selectedProfile.name && u.type === selectedProfile.type)?.pin}
              onSetPin={handleSetPin}
              onRemovePin={handleRemovePin}
            />
          )}
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button 
            className={`tab ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            Manage Roles
          </button>
          <button 
            className={`tab ${activeTab === 'household' ? 'active' : ''}`}
            onClick={() => setActiveTab('household')}
          >
            Household
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'users' && (
            <UsersTab users={users} roles={roles} onSave={saveUsers} />
          )}
          {activeTab === 'roles' && (
            <RolesTab roles={roles} onSave={saveRoles} />
          )}
          {activeTab === 'household' && (
            <HouseholdTab householdName={householdName} onSave={saveHouseholdName} />
          )}
        </div>
      </div>
    </div>
  );
}

// Users Tab Component
function UsersTab({ users, roles, onSave }) {
  const [editableUsers, setEditableUsers] = useState([...users]);

  useEffect(() => {
    // Ensure at least 4 slots
    while (editableUsers.length < 4) {
      editableUsers.push({ name: '', type: roles[0] || 'Admin', pin: '' });
    }
    setEditableUsers([...editableUsers]);
  }, []);

  const handleChange = (index, field, value) => {
    const updated = [...editableUsers];
    updated[index][field] = value;
    setEditableUsers(updated);
  };

  const handleSave = () => {
    const filtered = editableUsers.filter(u => u.name.trim() !== '');
    
    // Ensure at least one Admin
    if (!filtered.some(u => u.type === 'Admin')) {
      alert('At least one Admin user is required');
      return;
    }
    
    onSave(filtered);
  };

  return (
    <div className="users-tab">
      <div className="users-list">
        {editableUsers.slice(0, 4).map((user, index) => {
          const isFirstAdmin = index === 0;
          
          return (
            <div key={index} className="user-row">
              <div className="user-number">{index + 1}</div>
              <input
                type="text"
                placeholder="Name"
                value={user.name}
                onChange={(e) => handleChange(index, 'name', e.target.value)}
                className="user-input"
                disabled={isFirstAdmin}
                style={isFirstAdmin ? { background: '#f0f0f0', cursor: 'not-allowed' } : {}}
              />
              <select
                value={user.type}
                onChange={(e) => handleChange(index, 'type', e.target.value)}
                className="user-select"
                disabled={isFirstAdmin}
                style={isFirstAdmin ? { background: '#f0f0f0', cursor: 'not-allowed' } : {}}
              >
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      <div className="tab-actions">
        <button className="btn-primary" onClick={handleSave}>Save Users</button>
      </div>
      
      <div className="linked-users-section">
        <h4>Current Users</h4>
        <div className="linked-users-list">
          {users.map((user, index) => (
            <div key={index} className="linked-user-item">
              <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
              <div className="user-info">
                <span className="user-name">{user.name}</span>
                <span className="user-type badge">{user.type}</span>
                {index === 0 && <span className="admin-badge">Primary Admin</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Roles Tab Component
function RolesTab({ roles, onSave }) {
  const [editableRoles, setEditableRoles] = useState([...roles]);

  useEffect(() => {
    while (editableRoles.length < 4) {
      editableRoles.push('');
    }
    setEditableRoles([...editableRoles]);
  }, []);

  const handleChange = (index, value) => {
    const updated = [...editableRoles];
    updated[index] = value;
    setEditableRoles(updated);
  };

  const handleSave = () => {
    let filtered = editableRoles.filter(r => r.trim() !== '');
    filtered = [...new Set(filtered)]; // Remove duplicates
    
    // Ensure Admin is always first
    filtered = filtered.filter(r => r !== 'Admin');
    filtered.unshift('Admin');
    
    onSave(filtered);
  };

  return (
    <div className="roles-tab">
      <div className="roles-list">
        {editableRoles.slice(0, 4).map((role, index) => (
          <div key={index} className="role-row">
            <label>Role {index + 1}:</label>
            <input
              type="text"
              value={role}
              onChange={(e) => handleChange(index, e.target.value)}
              placeholder={index === 0 ? 'Admin (Required)' : 'Enter role name'}
              disabled={index === 0} // Admin is locked
              className="role-input"
            />
          </div>
        ))}
      </div>
      <div className="tab-actions">
        <button className="btn-primary" onClick={handleSave}>Save Roles</button>
      </div>
    </div>
  );
}

// Household Tab Component
function HouseholdTab({ householdName, onSave }) {
  const [name, setName] = useState(householdName);

  const handleSave = () => {
    if (name.trim() === '') {
      alert('Household name cannot be empty');
      return;
    }
    onSave(name);
  };

  return (
    <div className="household-tab">
      <div className="household-field">
        <label>Household Name:</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="household-input"
          placeholder="Enter household name"
        />
      </div>
      <div className="tab-actions">
        <button className="btn-primary" onClick={handleSave}>Save Household Name</button>
      </div>
    </div>
  );
}

// Set PIN Dialog Component
function SetPinDialog({ userName, userType, currentPin, onSetPin, onRemovePin }) {
  const [showDialog, setShowDialog] = useState(false);
  const [pin, setPin] = useState('');

  const handleSetPin = () => {
    if (pin.length < 4 || pin.length > 6) {
      alert('PIN must be 4-6 digits');
      return;
    }
    onSetPin(userName, userType, pin);
    setShowDialog(false);
    setPin('');
  };

  const handleRemovePin = () => {
    if (window.confirm('Are you sure you want to remove the PIN?')) {
      onRemovePin(userName, userType);
      setShowDialog(false);
    }
  };

  return (
    <>
      <button className="btn-set-pin" onClick={() => setShowDialog(true)}>
        {currentPin ? 'Change PIN' : 'Set PIN'}
      </button>

      {showDialog && (
        <div className="pin-dialog-overlay">
          <div className="pin-dialog">
            <div className="pin-dialog-header">
              <h3>Set PIN for {userName}</h3>
              <button className="btn-close" onClick={() => setShowDialog(false)}>✕</button>
            </div>
            <div className="pin-dialog-body">
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter 4-6 digit PIN"
                maxLength="6"
                className="pin-input"
              />
            </div>
            <div className="pin-dialog-actions">
              <button className="btn-secondary" onClick={() => setShowDialog(false)}>Cancel</button>
              {currentPin && (
                <button className="btn-danger" onClick={handleRemovePin}>Remove PIN</button>
              )}
              <button className="btn-primary" onClick={handleSetPin}>Save PIN</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UsersManagement;